import { create } from "zustand";
import Axios, { type AxiosInstance } from "axios";
import { IpcRest } from "@/hooks/ipc";
import loadStore from "@/utils/generateStore";
import { ErrorMapper } from "@/utils/errorMapping";
import { reportAxiosError } from "@/lib/sentryAxios";
import { Sentry } from "@/lib/sentry";
import { sessionTransport } from "@/platform";
import { installSessionInterceptors } from "@/context/auth/sessionAxios";
import { isIdentityToken } from "@/context/auth/sessionRenewal";

// Fábrica de instancias axios con la config común de la app (auth Bearer +
// businessId, refresh 401, mapeo de errores). Se extrae para poder crear una
// segunda instancia (clínica → BFF) sin duplicar los interceptors.
function createConfiguredAxios(baseURL: string): AxiosInstance {
  const instance = Axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  instance.interceptors.request.use(async (config) => {
    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    }

    return config;
  });

  installSessionInterceptors(instance, sessionTransport);

  instance.interceptors.response.use(
    (response) => response,

    async (error) => {
      // Manejo específico de errores 401 para refresh token
      // PERO no intentar refresh en endpoints de login
      const isLoginEndpoint =
        error.config?.url?.includes("/auth/local/signin") ||
        error.config?.url?.includes("/login") ||
        error.config?.url?.includes("/signin");

      if (
        error.response?.status === 401 &&
        !isLoginEndpoint &&
        !isIdentityToken(error.config?.sessionSnapshot?.token ?? null) &&
        !error.config?._legacyRefreshRetried
      ) {
        const store = await loadStore();
        const refreshToken = await store.get("refreshToken");
        const businessId = await store.get("businessId");

        if (refreshToken && businessId) {
          try {
            const refreshResponse = await IpcRest.post<any>({
              url: "/api/v1/auth/refresh",
              body: {},
              headers: {
                Authorization: `Bearer ${refreshToken}`,
                businessid: String(businessId),
              },
            });

            await store.set("token", refreshResponse.accessToken);
            await store.set("refreshToken", refreshResponse.refreshToken);

            error.config.headers.Authorization = `Bearer ${refreshResponse.accessToken}`;
            error.config._legacyRefreshRetried = true;
            delete error.config.sessionSnapshot;

            // Reintentar con la MISMA instancia (respeta su baseURL).
            return instance(error.config);
          } catch (refreshError) {
            // El refresh falló: auth está rota, no es ruido — siempre reportar.
            Sentry.captureException(refreshError, {
              tags: { source: "axios", auth_flow: "refresh_failed" },
            });
            // Limpiar tokens y redirigir al login
            await store.delete("token");
            await store.delete("refreshToken");
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }
      }

      // Reportar a Sentry con la forma original del error (antes del mapper,
      // que muta la shape y oculta detalles del axios original).
      reportAxiosError(error);

      // Procesar error con el nuevo ErrorMapper
      const processedError = {
        ...error,
        userMessage: ErrorMapper.processError(error),
        originalError: error.response?.data || error.message,
      };

      return Promise.reject(processedError);
    }
  );

  return instance;
}

// EL BORDE. Base única de la app: el BFF (PEP). trd-client no habla con
// trd-core ni con octopus — solo con su borde (trd-client#51 / trd-platform#14).
// `VITE_BFF_URL` no incluye `/api/v1`, por eso se compone acá.
//
// ⚠️ `VITE_API_ENDPOINT` (trd-core directo) quedó ERRADICADO del código: antes
// esta instancia lo usaba y arrastraba ~182 archivos a core. Ahora todo cae en
// el BFF; lo que el BFF todavía no expone responde 404 — ese 404 es la worklist
// de trudapp/trd-bff#98 (writes de core) y trudapp/trd-bff#97 (octopus).
export const BFF_API_BASE = `${import.meta.env.VITE_BFF_URL}/api/v1`;

// Instancia principal (`useAxios`). La usa el resto de la app → ahora al BFF.
const axios = createConfiguredAxios(BFF_API_BASE);

// Instancia "clinical": misma base que la principal (es el axios genérico del
// BFF, sólo mal nombrado por historia). Se mantiene por compatibilidad.
const clinicalAxios = createConfiguredAxios(BFF_API_BASE);

const useAxiosStore = create(() => ({
  axios: axios,
  clinicalAxios: clinicalAxios,
}));

export const useAxios = () => {
  const axios = useAxiosStore((state) => state.axios);

  return axios;
};

// Axios del dominio clínico → BFF. Usado por los hooks de `react-query/Clinical`.
export const useClinicalAxios = () => {
  const clinicalAxios = useAxiosStore((state) => state.clinicalAxios);

  return clinicalAxios;
};

// Alias canónico del axios del BFF. La instancia "clinical" es en realidad el
// axios genérico del BFF (misma config, base VITE_BFF_URL/api/v1); los flips de
// #51 (dominios que dejan de pegar a trd-core directo → todo por el BFF) usan
// este nombre. `useClinicalAxios` queda como alias del código clínico existente.
export const useBffAxios = useClinicalAxios;
